import React from 'react';
import { Pie } from 'react-chartjs-2';
import { theme } from '../../utils/theme';

const WorkerStats = ({ workerStats, workerHistory, workerStatsPagination, onPageChange }) => {
    const tableStyles = {
        table: "min-w-full",
        header: "border-b border-[#6D9773]/20",
        headerCell: "px-6 py-3 text-left text-xs font-medium text-[#0C3B2E] uppercase tracking-wider",
        row: "border-b border-[#6D9773]/10 hover:bg-[#6D9773]/5 transition-colors",
        cell: "px-6 py-4 text-sm text-[#0C3B2E]"
    };

    const renderTaskData = () => {
        if (!workerStats) return null;

        const taskData = {
            labels: ['Items Processed', 'Successful', 'Buckets Deleted'],
            datasets: [{
                data: [
                    workerStats.tasksProcessed.itemsProcessed,
                    workerStats.tasksProcessed.successCount,
                    workerStats.tasksProcessed.deletedBuckets
                ],
                backgroundColor: [
                    '#6D9773',
                    '#80ab87',
                    '#95bf9c'
                ],
                borderColor: '#ffffff',
                borderWidth: 2
            }]
        };

        return (
            <Pie
                data={taskData}
                options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            position: 'bottom'
                        }
                    }
                }}
            />
        );
    };

    return (
        <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className={theme.card.base}>
                    <h3 className="text-lg font-medium text-[#6D9773] mb-4">Worker Status</h3>
                    <div className="space-y-4">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Last Run</p>
                            <p className="text-2xl font-semibold">
                                {workerStats ? new Date(workerStats.runAt).toLocaleTimeString() : '-'}
                            </p>
                            <p className="text-xs text-gray-500">
                                {workerStats ? new Date(workerStats.runAt).toLocaleDateString() : '-'}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-600">Tasks Overview</p>
                            <div className="mt-4 h-64">
                                {renderTaskData()}
                            </div>
                        </div>
                    </div>
                </div>

                <div className={theme.card.base}>
                    <h3 className="text-lg font-medium text-[#6D9773] mb-4">Task Details</h3>
                    <div className="space-y-6">
                        <div>
                            <h4 className="text-md font-medium mb-2">Deletion Reminders</h4>
                            <p className="text-2xl font-semibold">
                                {workerStats?.tasksProcessed.deletionReminders || 0}
                            </p>
                            <p className="text-xs text-gray-500">Active notification configs</p>
                        </div>
                        <div>
                            <h4 className="text-md font-medium mb-2">Unpaid Bucket Reminders</h4>
                            <p className="text-2xl font-semibold">
                                {workerStats?.tasksProcessed.unpaidReminders || 0}
                            </p>
                            <p className="text-xs text-gray-500">Pending notifications</p>
                        </div>
                        <div>
                            <h4 className="text-md font-medium mb-2">Buckets Pending Deletion</h4>
                            <p className="text-2xl font-semibold">
                                {workerStats?.tasksProcessed.deletedBuckets || 0}
                            </p>
                            <p className="text-xs text-gray-500">Will be processed at next run</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className={theme.card.base}>
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-[#6D9773]">Recent Activity</h3>
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">
                            Page {workerStatsPagination.currentPage} of {workerStatsPagination.pages}
                        </span>
                        <div className="flex gap-2">
                            <button
                                onClick={() => onPageChange(Math.max(1, workerStatsPagination.currentPage - 1))}
                                disabled={workerStatsPagination.currentPage === 1}
                                className={`px-3 py-1 text-sm rounded border ${
                                    workerStatsPagination.currentPage === 1 
                                    ? 'bg-gray-100 text-gray-400 border-gray-200'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => onPageChange(Math.min(workerStatsPagination.pages, workerStatsPagination.currentPage + 1))}
                                disabled={workerStatsPagination.currentPage === workerStatsPagination.pages}
                                className={`px-3 py-1 text-sm rounded border ${
                                    workerStatsPagination.currentPage === workerStatsPagination.pages
                                    ? 'bg-gray-100 text-gray-400 border-gray-200'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className={tableStyles.table}>
                        <thead className={tableStyles.header}>
                            <tr>
                                <th className={tableStyles.headerCell}>Timestamp</th>
                                <th className={tableStyles.headerCell}>Job Type</th>
                                <th className={tableStyles.headerCell}>Items Processed</th>
                                <th className={tableStyles.headerCell}>Success Count</th>
                                <th className={tableStyles.headerCell}>Buckets Deleted</th>
                                <th className={tableStyles.headerCell}>Duration</th>
                                <th className={tableStyles.headerCell}>Status</th>
                                <th className={tableStyles.headerCell}>Errors</th>
                            </tr>
                        </thead>
                        <tbody>
                            {workerHistory.map((stat, index) => (
                                <tr key={index} className={tableStyles.row}>
                                    <td className={tableStyles.cell}>
                                        {new Date(stat.runAt).toLocaleString()}
                                    </td>
                                    <td className={tableStyles.cell}>
                                        {stat.taskType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                    </td>
                                    <td className={`${tableStyles.cell} text-center`}>
                                        {stat.itemsProcessed}
                                    </td>
                                    <td className={`${tableStyles.cell} text-center`}>
                                        {stat.successCount}
                                    </td>
                                    <td className={`${tableStyles.cell} text-center`}>
                                        {stat.deletedBuckets || 0}
                                    </td>
                                    <td className={tableStyles.cell}>
                                        {stat.duration ? `${(stat.duration / 1000).toFixed(2)}s` : 'N/A'}
                                    </td>
                                    <td className={tableStyles.cell}>
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                            stat.status === 'success' 
                                                ? 'bg-green-100 text-green-800'
                                                : stat.status === 'partial_failure'
                                                ? 'bg-yellow-100 text-yellow-800'
                                                : 'bg-red-100 text-red-800'
                                        }`}>
                                            {stat.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                        </span>
                                    </td>
                                    <td className={tableStyles.cell}>
                                        {stat.errors?.length > 0 ? (
                                            <div className="group relative">
                                                <button 
                                                    className="text-red-600 hover:text-red-800"
                                                    title="View errors"
                                                >
                                                    {stat.errors.length} {stat.errors.length === 1 ? 'error' : 'errors'}
                                                </button>
                                                <div className="hidden group-hover:block absolute z-10 bg-white rounded-lg shadow-lg p-4 min-w-[300px] right-0">
                                                    <h4 className="font-medium mb-2">Errors:</h4>
                                                    <ul className="list-disc pl-4 space-y-1 text-sm">
                                                        {stat.errors.map((error, i) => (
                                                            <li key={i} className="text-red-600">
                                                                {error.error}
                                                                <br />
                                                                <span className="text-xs text-gray-500">
                                                                    Attempt {error.attempt} at {new Date(error.timestamp).toLocaleString()}
                                                                </span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        ) : (
                                            <span className="text-green-600">None</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
};

export default WorkerStats;